import type {
  CurrentTournamentResponse,
  LeaderboardDto,
  SharePrepareRequest,
  SharePrepareResponse,
  ShopDto,
  EconomySettings,
  AdminEconomySettingDto,
  AdminActionDto,
  AdminAlertDto,
  AdminClaimDto,
  AdminOverviewDto,
  AdminSeasonDto,
  AdminUserDto,
  AdminUserRowDto,
  TokenClaimStateDto,
  WalletDto,
  SeasonUserDto,
  EconomyBalanceDto,
  SilverGoldExchangeResponse,
  ReferralsDto,
  TicketsDto,
  TrainingBatchDto,
  TrainingFinishDto,
  TrainingStartDto,
  BuyTicketResponse,
  FriendsDto,
  InviteFriendResponse,
  StarsInvoiceResponse,
  MeDto,
  TelegramAuthRequest,
  TelegramAuthResponse,
  TournamentDto,
  UpdateProfileRequest,
} from '@penalty-cup/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit & { token?: string | null } = {}): Promise<T> {
  const { token, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...rest,
      headers: {
        ...(rest.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  if (res.status === 204) return null as T;
  if (!res.ok) {
    // Код ошибки сервера ({ error: 'invite_expired' }) — чтобы показать понятный текст
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    throw new ApiError(res.status, typeof body?.error === 'string' ? body.error : res.statusText);
  }
  return (await res.json()) as T;
}

const get = <T>(token: string, path: string) => request<T>(path, { token });
const post = <T>(token: string, path: string, body?: object) =>
  request<T>(path, { method: 'POST', token, ...(body ? { body: JSON.stringify(body) } : {}) });

export const api = {
  /** Админка (сервер отвечает только ADMIN_TELEGRAM_IDS) */
  admin: {
    overview: (t: string) => get<AdminOverviewDto>(t, '/admin/overview'),
    seasons: (t: string) => get<AdminSeasonDto[]>(t, '/admin/seasons'),
    createSeason: (t: string, b: { tokenPool: number; days: number; real: boolean }) =>
      post<AdminSeasonDto[]>(t, '/admin/seasons', b),
    claims: (t: string, status: string) =>
      get<AdminClaimDto[]>(t, `/admin/claims${status ? `?status=${encodeURIComponent(status)}` : ''}`),
    resolveClaim: (t: string, id: string, b: { confirmed: string } | { failed: string }) =>
      post<AdminClaimDto[]>(t, `/admin/claims/${encodeURIComponent(id)}/resolve`, b),
    users: (t: string, q: string) => get<AdminUserRowDto[]>(t, `/admin/users?q=${encodeURIComponent(q)}`),
    user: (t: string, id: number) => get<AdminUserDto>(t, `/admin/users/${id}`),
    flag: (t: string, id: number, reason: string) =>
      post<AdminUserDto>(t, `/admin/users/${id}/flag`, { reason }),
    unflag: (t: string, id: number) => post<AdminUserDto>(t, `/admin/users/${id}/unflag`),
    actions: (t: string) => get<AdminActionDto[]>(t, '/admin/actions'),
    economy: (t: string) => get<AdminEconomySettingDto[]>(t, '/admin/economy'),
    alerts: (t: string) => get<AdminAlertDto[]>(t, '/admin/alerts'),
    ackAlert: (t: string, id: string) =>
      post<AdminAlertDto[]>(t, `/admin/alerts/${encodeURIComponent(id)}/ack`),
    runAlerts: (t: string) => post<AdminAlertDto[]>(t, '/admin/alerts/run'),
    setEconomy: (t: string, changes: Record<string, number>) =>
      post<AdminEconomySettingDto[]>(t, '/admin/economy', { changes }),
  },
  economySettings: (token: string) => request<EconomySettings>('/economy/settings', { token }),
  economyBalance: (token: string) => request<EconomyBalanceDto>('/economy/balance', { token }),
  exchangeSilverGold: (token: string, amountGold: number) =>
    request<SilverGoldExchangeResponse>('/economy/exchange/silver-gold', {
      method: 'POST',
      token,
      body: JSON.stringify({ amountGold }),
    }),
  seasonUser: (token: string) => request<SeasonUserDto>('/seasons/current/user', { token }),
  commitGold: (token: string, amount: number) =>
    request<SeasonUserDto>('/seasons/current/commit-gold', {
      method: 'POST',
      token,
      body: JSON.stringify({ amount }),
    }),
  walletNonce: (token: string) =>
    request<{ payload: string }>('/wallet/connect/nonce', { method: 'POST', token }),
  walletVerify: (token: string, body: object) =>
    request<WalletDto>('/wallet/connect/verify', { method: 'POST', token, body: JSON.stringify(body) }),
  tokenState: (token: string) => request<TokenClaimStateDto>('/token/claim', { token }),
  tokenClaim: (token: string, season: number) =>
    request<TokenClaimStateDto>('/token/claim', { method: 'POST', token, body: JSON.stringify({ season }) }),
  referrals: (token: string) => request<ReferralsDto>('/referrals', { token }),
  friends: (token: string) => request<FriendsDto>('/friends', { token }),
  inviteFriend: (token: string, tournamentId: string, friendId: number) =>
    request<InviteFriendResponse>('/friends/invite', {
      method: 'POST',
      token,
      body: JSON.stringify({ tournamentId, friendId }),
    }),
  declineInvite: (token: string, code: string) =>
    request<null>('/friends/decline', { method: 'POST', token, body: JSON.stringify({ code }) }),
  shop: (token: string) => request<ShopDto>('/shop', { token }),
  buyItem: (token: string, itemId: string) =>
    request<ShopDto>('/shop/buy', { method: 'POST', token, body: JSON.stringify({ itemId }) }),
  equipItem: (token: string, slot: string, itemId: string) =>
    request<ShopDto>('/shop/equip', { method: 'POST', token, body: JSON.stringify({ slot, itemId }) }),
  starsInvoice: (token: string, itemId: string) =>
    request<StarsInvoiceResponse>('/shop/stars-invoice', {
      method: 'POST',
      token,
      body: JSON.stringify({ itemId }),
    }),
  loginWithTelegram: (initData: string) =>
    request<TelegramAuthResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData } satisfies TelegramAuthRequest),
    }),
  me: (token: string) => request<MeDto>('/me', { token }),
  updateProfile: (token: string, patch: UpdateProfileRequest) =>
    request<MeDto>('/me/profile', { method: 'PATCH', token, body: JSON.stringify(patch) }),
  joinTournament: (token: string, size: 4 | 8 = 8) =>
    request<TournamentDto>('/tournaments/join', { method: 'POST', token, body: JSON.stringify({ size }) }),
  trainingStart: (token: string, source: 'home' | 'not_enough_coins' = 'home') =>
    request<TrainingStartDto>('/training/start', { method: 'POST', token, body: JSON.stringify({ source }) }),
  trainingTaps: (
    token: string,
    sessionId: string,
    taps: number,
    durationMs: number,
    clientBatchId?: string,
  ) =>
    request<TrainingBatchDto>('/training/taps', {
      method: 'POST',
      token,
      body: JSON.stringify({ sessionId, taps, durationMs, clientBatchId }),
    }),
  trainingFinish: (token: string, sessionId: string) =>
    request<TrainingFinishDto>('/training/finish', {
      method: 'POST',
      token,
      body: JSON.stringify({ sessionId }),
    }),
  quickMatch: (token: string) => request<TournamentDto>('/tournaments/quick', { method: 'POST', token }),
  tickets: (token: string) => request<TicketsDto>('/tickets', { token }),
  ticketSlotInvoice: (token: string) =>
    request<StarsInvoiceResponse>('/tickets/slot-invoice', { method: 'POST', token }),
  buyTicket: (token: string) => request<BuyTicketResponse>('/tickets/buy', { method: 'POST', token }),
  createDuel: (token: string) => request<TournamentDto>('/tournaments/duel', { method: 'POST', token }),
  createFriendsCup: (token: string, size: 4 | 8) =>
    request<TournamentDto>('/tournaments/friends', { method: 'POST', token, body: JSON.stringify({ size }) }),
  joinInvite: (token: string, code: string) =>
    request<TournamentDto>(`/tournaments/invite/${encodeURIComponent(code)}`, { method: 'POST', token }),
  startTournament: (token: string, id: string) =>
    request<TournamentDto>(`/tournaments/${id}/start`, { method: 'POST', token }),
  leaveTournament: (token: string, id: string) =>
    request<null>(`/tournaments/${id}/leave`, { method: 'POST', token }),
  prepareShare: (token: string, req: SharePrepareRequest) =>
    request<SharePrepareResponse>('/share/prepare', { method: 'POST', token, body: JSON.stringify(req) }),
  leaderboard: (token: string) => request<LeaderboardDto>('/leaderboard', { token }),
  currentTournament: (token: string) => request<CurrentTournamentResponse>('/tournaments/current', { token }),
};
